import {
  AllowedCharactersSchema,
  ApplyingRule,
  CharTypes,
  ConditionSet,
  IFieldElementUnion,
  IFormSchemaV2,
  MaxLengthValidationSchema,
  RadioButtonsElement,
  SectionElement,
  IPostalCodeInputElement,
  isPostalCodeInputElement,
  isInputElement,
  ICorporateSearchElement,
  isCorporateSearchElement,
  ITextInputElement,
} from "@graffer-inc/form-schema";
import type { ApplicantTypeLiterals } from "../../../application/applicant";
import { cleanUndefined } from "../../../../utils/clean-properties";
import {
  applicantUnSupportedProperties,
  ReservedApplicantIdHelper,
} from "./applicant-reserved-ids";
import {
  ApplicantSchemaKeys,
  DefaultApplicantSchemaKeys,
} from "../applicant-schema-keys";

/**
 * 特定申請者の種別において、サポートされていない項目に対するルールかどうかを判定する
 */
export function checkNotSupportedRule(
  ifSet: ConditionSet,
  then: ApplyingRule,
  applicantSchemaKeys: ApplicantSchemaKeys,
) {
  const ifCond = ifSet.conditions.length === 1 ? ifSet.conditions[0] : false;
  if (
    !ifCond ||
    !ReservedApplicantIdHelper.isApplicantType(ifCond.id, applicantSchemaKeys)
  ) {
    return false;
  }

  return ReservedApplicantIdHelper.isUnsupported(
    then.id,
    ifCond.match.const as ApplicantTypeLiterals,
    applicantSchemaKeys,
  );
}

// 初期データの生成にのみ利用し、 export しない。
const ReservedApplicantIDRegistry = {
  applicantSection: "applicantSection",
  applicantType: "applicantType",
  applicantCorporateSearch: "applicantCorporateSearch",
  applicantName: "applicantName",
  applicantNameKana: "applicantNameKana",
  applicantPostalCode: "applicantPostalCode",
  applicantAddress: "applicantAddress",
  applicantBirthDate: "applicantBirthDate",
  applicantTradeName: "applicantTradeName",
  applicantTradeNameKana: "applicantTradeNameKana",
  applicantCorporateNumber: "applicantCorporateNumber",
  applicantRepresentativeName: "applicantRepresentativeName",
  // モデルの変数名(applicant.tel)と異なっているため注意
  applicantPhoneNumber: "applicantPhoneNumber",
  applicantEmail: "applicantEmail",
  applicantContactPersonal: "applicantContactPersonal",
} as const;

/**
 * @deprecated
 * テストコードでの利用にとどめること。
 */
export const ReservedApplicantIDRegistryForTesting = {
  ...ReservedApplicantIDRegistry,
} as const;

// 初期データの生成にのみ利用し、 export しない。
const ApplicantDefaultHiddenProperties = {
  INDIVIDUAL: [
    ReservedApplicantIDRegistry.applicantTradeName,
    ReservedApplicantIDRegistry.applicantTradeNameKana,
  ],
  CORPORATE: [],
  UNINCORPORATED_GROUP: [],
} as const;

// 初期データの生成にのみ利用し、 export しない。
const ApplicantUnSupportedProperties = {
  INDIVIDUAL: applicantUnSupportedProperties(
    "INDIVIDUAL",
    DefaultApplicantSchemaKeys,
  ),
  CORPORATE: applicantUnSupportedProperties(
    "CORPORATE",
    DefaultApplicantSchemaKeys,
  ),
  UNINCORPORATED_GROUP: applicantUnSupportedProperties(
    "UNINCORPORATED_GROUP",
    DefaultApplicantSchemaKeys,
  ),
} as const;

const KATAKANA_VALIDATION: AllowedCharactersSchema = Object.freeze({
  keyword: "allowedCharacters" as const,
  value: new Array<CharTypes>(
    "katakana",
    "half_width_space",
    "full_width_space",
  ),
});

const MAX_LENGTH_255: MaxLengthValidationSchema = Object.freeze({
  keyword: "maxLength",
  value: 255,
});

const legacyApplicantPostalCode: ITextInputElement = {
  id: ReservedApplicantIDRegistry.applicantPostalCode,
  label: {
    text: "郵便番号",
    shouldDisplay: true,
  },
  inputType: "text",
  required: true,
  hidden: false,
  description: {
    text: "ハイフンなしの半角7桁で入力してください",
    shouldDisplay: true,
    isMarkdown: true,
  },
  elementType: "input",
  placeholder: "",
  validations: [
    {
      keyword: "postalCode",
    },
  ],
};

export const LegacyComponent = {
  applicantPostalCode: legacyApplicantPostalCode,
} as const;

const replacedApplicantPostalCode: IPostalCodeInputElement = {
  id: ReservedApplicantIDRegistry.applicantPostalCode,
  label: {
    text: "郵便番号",
    shouldDisplay: true,
  },
  inputType: "postal_code",
  required: true,
  hidden: false,
  description: {
    text: "ハイフンなしの半角7桁で入力してください",
    shouldDisplay: true,
    isMarkdown: true,
  },
  elementType: "input",
  validations: [
    {
      keyword: "postalCode",
    },
  ],
  addressCompleteTarget: ReservedApplicantIDRegistry.applicantAddress,
};

const replacedApplicantCorporateSearch: ICorporateSearchElement = {
  id: ReservedApplicantIDRegistry.applicantCorporateSearch,
  elementType: "function",
  functionType: "corporate_search",
  label: {
    shouldDisplay: false,
    text: "法人検索",
  },
  hidden: false,
  autocompleteTarget: {
    corporateName: ReservedApplicantIDRegistry.applicantName,
    corporateNumber: ReservedApplicantIDRegistry.applicantCorporateNumber,
    corporateAddress: ReservedApplicantIDRegistry.applicantAddress,
    corporatePostalCode: ReservedApplicantIDRegistry.applicantPostalCode,
  },
};

export const ReplacedComponent = {
  applicantPostalCode: replacedApplicantPostalCode,
  applicantCorporateSearch: replacedApplicantCorporateSearch,
} as const;

export const isReplacedApplicantPostalCodeComponent = (
  element: IFieldElementUnion,
  applicantSchemaKeys: ApplicantSchemaKeys,
): element is IPostalCodeInputElement => {
  return (
    element.id === applicantSchemaKeys.applicantPostalCode.id &&
    isInputElement(element) &&
    isPostalCodeInputElement(element)
  );
};

export const isReplacedApplicantCorporateSearchComponent = (
  element: IFieldElementUnion,
  applicantSchemaKeys: ApplicantSchemaKeys,
): element is ICorporateSearchElement => {
  return (
    element.id === applicantSchemaKeys.applicantCorporateSearch.id &&
    isCorporateSearchElement(element)
  );
};

// 初期データの生成にのみ利用し、 export しない。
const ReservedApplicantSchemas: IFieldElementUnion[] = [
  {
    id: ReservedApplicantIDRegistry.applicantType,
    elementType: "input",
    inputType: "radio_buttons",
    label: {
      text: "申請者の種別",
      shouldDisplay: true,
    },
    description: {
      text: "",
      shouldDisplay: true,
      isMarkdown: true,
    },
    required: true,
    enum: [
      {
        text: "個人",
        value: "INDIVIDUAL",
      },
      {
        text: "法人",
        value: "CORPORATE",
      },
      {
        text: "団体",
        value: "UNINCORPORATED_GROUP",
      },
    ],
    validations: [],
  },
  // ReplacedComponent.applicantCorporateSearch,
  {
    id: ReservedApplicantIDRegistry.applicantName,
    label: {
      text: "名前",
      shouldDisplay: true,
    },
    required: true,
    hidden: false,
    inputType: "text",
    description: {
      text: "",
      shouldDisplay: true,
      isMarkdown: true,
    },
    elementType: "input",
    placeholder: "",
    validations: [MAX_LENGTH_255],
  },
  {
    id: ReservedApplicantIDRegistry.applicantNameKana,
    label: {
      text: "名前（カナ）",
      shouldDisplay: true,
    },
    required: true,
    hidden: false,
    inputType: "text",
    description: {
      text: "",
      shouldDisplay: true,
      isMarkdown: true,
    },
    elementType: "input",
    placeholder: "",
    validations: [MAX_LENGTH_255, KATAKANA_VALIDATION],
  },
  LegacyComponent.applicantPostalCode,
  {
    id: ReservedApplicantIDRegistry.applicantAddress,
    label: {
      text: "住所 / 所在地",
      shouldDisplay: true,
    },
    inputType: "text",
    required: true,
    hidden: false,
    description: {
      text: "",
      shouldDisplay: true,
      isMarkdown: true,
    },
    elementType: "input",
    placeholder: "",
    validations: [MAX_LENGTH_255],
  },
  {
    id: ReservedApplicantIDRegistry.applicantBirthDate,
    label: {
      text: "生年月日（西暦）",
      shouldDisplay: true,
    },
    dateFormat: "YYYY/MM/DD",
    required: true,
    hidden: false,
    inputType: "date",
    description: {
      text: "",
      shouldDisplay: true,
      isMarkdown: true,
    },
    elementType: "input",
    validations: [],
    formType: "MultiInput",
  },
  {
    id: ReservedApplicantIDRegistry.applicantTradeName,
    label: {
      text: "屋号",
      shouldDisplay: true,
    },
    required: true,
    hidden: false,
    inputType: "text",
    description: {
      text: "",
      shouldDisplay: true,
      isMarkdown: true,
    },
    elementType: "input",
    placeholder: "",
    validations: [MAX_LENGTH_255],
  },
  {
    id: ReservedApplicantIDRegistry.applicantTradeNameKana,
    label: {
      text: "屋号（カナ）",
      shouldDisplay: true,
    },
    required: true,
    hidden: false,
    inputType: "text",
    description: {
      text: "",
      shouldDisplay: true,
      isMarkdown: true,
    },
    elementType: "input",
    placeholder: "",
    validations: [MAX_LENGTH_255, KATAKANA_VALIDATION],
  },
  {
    id: ReservedApplicantIDRegistry.applicantCorporateNumber,
    label: {
      text: "法人番号",
      shouldDisplay: true,
    },
    required: true,
    hidden: false,
    inputType: "text",
    description: {
      text: "",
      shouldDisplay: true,
      isMarkdown: true,
    },
    elementType: "input",
    placeholder: "",
    validations: [
      {
        keyword: "corporateNumber",
      },
    ],
  },
  {
    id: ReservedApplicantIDRegistry.applicantRepresentativeName,
    label: {
      text: "法人代表者名",
      shouldDisplay: true,
    },
    required: true,
    hidden: false,
    inputType: "text",
    description: {
      text: "",
      shouldDisplay: true,
      isMarkdown: true,
    },
    elementType: "input",
    placeholder: "",
    validations: [MAX_LENGTH_255],
  },
  {
    id: ReservedApplicantIDRegistry.applicantPhoneNumber,
    label: {
      text: "電話番号",
      shouldDisplay: true,
    },
    required: true,
    hidden: false,
    inputType: "text",
    description: {
      text: "日中に連絡が取れる電話番号を入力してください",
      shouldDisplay: true,
      isMarkdown: true,
    },
    elementType: "input",
    placeholder: "",
    validations: [
      {
        keyword: "maxLength",
        value: 32,
      },
    ],
  },
  {
    id: ReservedApplicantIDRegistry.applicantEmail,
    label: {
      text: "メールアドレス",
      shouldDisplay: true,
    },
    required: true,
    hidden: false,
    inputType: "email",
    description: {
      text: "",
      shouldDisplay: true,
      isMarkdown: true,
    },
    elementType: "input",
    placeholder: "",
    validations: [],
  },
  {
    id: ReservedApplicantIDRegistry.applicantContactPersonal,
    label: {
      text: "連絡担当者名",
      shouldDisplay: true,
    },
    required: true,
    hidden: false,
    inputType: "text",
    description: {
      text: "申請内容に確認が必要な際に連絡することがあるため、担当者の氏名を入力してください",
      shouldDisplay: true,
      isMarkdown: true,
    },
    elementType: "input",
    placeholder: "",
    validations: [MAX_LENGTH_255],
  },
];

// 初期データの生成にのみ利用し、 export しない。
const ApplicantSection: SectionElement = Object.freeze(
  SectionElement.create({
    id: ReservedApplicantIDRegistry.applicantSection,
    title: "申請者の情報",
    elementType: "section",
    children: ReservedApplicantSchemas,
  }),
);

/**
 * @deprecated
 * テストコードでの利用にとどめること。
 */
export const ApplicantSectionForTesting = { ...ApplicantSection };

// 初期データの生成にのみ利用し、 export しない。
const InitialApplicantRules: {
  [k in ApplicantTypeLiterals]: {
    [v: string]: ApplyingRule;
  };
} = Object.freeze({
  INDIVIDUAL: ApplicantSection.children
    .filter((e) => e.id !== ReservedApplicantIDRegistry.applicantType)
    .reduce(
      (prevV, curV) => ({
        ...prevV,
        [curV.id]: {
          id: curV.id,
          rule: cleanUndefined({
            label:
              curV.id === ReservedApplicantIDRegistry.applicantAddress
                ? {
                    text: "住所",
                  }
                : undefined,
            required: [
              ...ApplicantUnSupportedProperties.INDIVIDUAL,
              ...ApplicantDefaultHiddenProperties.INDIVIDUAL,
            ].every((id) => id !== curV.id),
            hidden: [
              ...ApplicantUnSupportedProperties.INDIVIDUAL,
              ...ApplicantDefaultHiddenProperties.INDIVIDUAL,
            ].some((id) => id === curV.id),
          }),
        },
      }),
      {},
    ),
  CORPORATE: ApplicantSection.children
    .filter((e) => e.id !== ReservedApplicantIDRegistry.applicantType)
    .reduce(
      (prevV, curV) => ({
        ...prevV,
        [curV.id]: {
          id: curV.id,
          rule: cleanUndefined({
            label: (() => {
              switch (curV.id) {
                case ReservedApplicantIDRegistry.applicantAddress:
                  return {
                    text: "本店所在地",
                  };
                case ReservedApplicantIDRegistry.applicantName:
                  return {
                    text: "法人名",
                  };
                case ReservedApplicantIDRegistry.applicantNameKana:
                  return {
                    text: "法人名（カナ）",
                  };
                default:
                  return undefined;
              }
            })(),
            required: [
              ...ApplicantUnSupportedProperties.CORPORATE,
              ...ApplicantDefaultHiddenProperties.CORPORATE,
            ].every((id) => id !== curV.id),
            hidden: [
              ...ApplicantUnSupportedProperties.CORPORATE,
              ...ApplicantDefaultHiddenProperties.CORPORATE,
            ].some((id) => id === curV.id),
          }),
        },
      }),
      {},
    ),
  UNINCORPORATED_GROUP: ApplicantSection.children
    .filter((e) => e.id !== ReservedApplicantIDRegistry.applicantType)
    .reduce(
      (prevV, curV) => ({
        ...prevV,
        [curV.id]: {
          id: curV.id,
          rule: cleanUndefined({
            label: (() => {
              switch (curV.id) {
                case ReservedApplicantIDRegistry.applicantAddress:
                  return {
                    text: "所在地",
                  };
                case ReservedApplicantIDRegistry.applicantName:
                  return {
                    text: "団体名",
                  };
                case ReservedApplicantIDRegistry.applicantNameKana:
                  return {
                    text: "団体名（カナ）",
                  };
                case ReservedApplicantIDRegistry.applicantRepresentativeName:
                  return {
                    text: "代表者名",
                  };
                default:
                  return undefined;
              }
            })(),
            required: [
              ...ApplicantUnSupportedProperties.UNINCORPORATED_GROUP,
              ...ApplicantDefaultHiddenProperties.UNINCORPORATED_GROUP,
            ].every((id) => id !== curV.id),
            hidden: [
              ...ApplicantUnSupportedProperties.UNINCORPORATED_GROUP,
              ...ApplicantDefaultHiddenProperties.UNINCORPORATED_GROUP,
            ].some((id) => id === curV.id),
          }),
        },
      }),
      {},
    ),
});

/**
 * @deprecated
 * テストコードでの利用にとどめること。
 */
export const InitialApplicantRulesForTesting = { ...InitialApplicantRules };

const InitialFormSchema: IFormSchemaV2 = Object.freeze({
  root: [ApplicantSection],
  version: "1.0.0",
  conditionalRuleSet: {
    conditionalRules: [
      {
        if: {
          conditions: [
            {
              id: ReservedApplicantIDRegistry.applicantType,
              match: {
                const: "INDIVIDUAL",
              },
            },
          ],
        },
        then: {
          rules: Object.keys(InitialApplicantRules["INDIVIDUAL"]).map(
            (k) => InitialApplicantRules["INDIVIDUAL"][k],
          ),
        },
      },
      {
        if: {
          conditions: [
            {
              id: ReservedApplicantIDRegistry.applicantType,
              match: {
                const: "CORPORATE",
              },
            },
          ],
        },
        then: {
          rules: Object.keys(InitialApplicantRules["CORPORATE"]).map(
            (k) => InitialApplicantRules["CORPORATE"][k],
          ),
        },
      },
      {
        if: {
          conditions: [
            {
              id: ReservedApplicantIDRegistry.applicantType,
              match: {
                const: "UNINCORPORATED_GROUP",
              },
            },
          ],
        },
        then: {
          rules: Object.keys(InitialApplicantRules["UNINCORPORATED_GROUP"]).map(
            (k) => InitialApplicantRules["UNINCORPORATED_GROUP"][k],
          ),
        },
      },
    ],
  },
});

export const createInitialCloudGovSchema = (
  supported: readonly ApplicantTypeLiterals[],
): IFormSchemaV2 => {
  if (supported.length === 0) {
    throw new Error(`ApplicantType must be supported at least one type.`);
  }
  const copied: IFormSchemaV2 = JSON.parse(JSON.stringify(InitialFormSchema));
  (copied.root[0].children[0] as RadioButtonsElement).enum = (
    copied.root[0].children[0] as RadioButtonsElement
  ).enum.filter(
    (e) => supported.indexOf(e.value as ApplicantTypeLiterals) !== -1,
  );
  copied.conditionalRuleSet!.conditionalRules =
    copied.conditionalRuleSet!.conditionalRules.filter(
      (r) =>
        supported.indexOf(
          r.if.conditions[0].match.const as ApplicantTypeLiterals,
        ) !== -1,
    );
  return copied;
};

export const getDefaultApplicantSectionComponentConfigToCompare = (
  elem: IFieldElementUnion,
  applicantSchemaKeys: ApplicantSchemaKeys,
): { element?: IFieldElementUnion; defaultIndex: number } => {
  const keys = Object.keys(
    applicantSchemaKeys,
  ) as (keyof typeof applicantSchemaKeys)[];
  const defaultIdFittingApplicantSection = keys.find((key) => {
    const schemaKey = applicantSchemaKeys[key];
    return schemaKey.id === elem.id;
  });
  if (typeof defaultIdFittingApplicantSection === "undefined") {
    return { defaultIndex: -1 };
  }

  const defaultIndex = ApplicantSection.children.findIndex(
    (e) => e.id === defaultIdFittingApplicantSection,
  );
  if (defaultIndex === -1) {
    if (
      isReplacedApplicantCorporateSearchComponent(elem, applicantSchemaKeys)
    ) {
      return {
        element: ReplacedComponent.applicantCorporateSearch,
        defaultIndex,
      };
    }
    return { defaultIndex };
  }

  const defaultElement = ApplicantSection.children[defaultIndex];

  return {
    element: isReplacedApplicantPostalCodeComponent(elem, applicantSchemaKeys)
      ? ReplacedComponent.applicantPostalCode
      : defaultElement,
    defaultIndex,
  };
};
